import 'react-tabs/style/react-tabs.css'
import 'leaflet/dist/leaflet.css'

import styled from '@emotion/styled'
import { Button } from 'app/components/Common/Button'
import { Separator } from 'app/components/Common/Separator'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { useVocabularyData } from 'app/utils/vocabulary'
import L from 'leaflet'
import React, { memo, useEffect, useState } from 'react'
import * as Icon from 'react-feather'
import LazyLoad from 'react-lazyload'
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet'
import Fade from 'react-reveal/Fade'
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs'

import { TabIcon } from './TabIcon'

export interface Props {
  address?: string
  directions?: { [key: string]: any }
  googleMapsURL?: string
  image?: string
  languageCode: string
  latitude?: string
  longitude?: string
  siteName?: string
  title?: string
  zoom?: string
}

export const Directions = memo(function Directions({
  address,
  directions,
  googleMapsURL,
  image,
  languageCode,
  latitude,
  longitude,
  siteName,
  title,
  zoom,
}: Props) {
  const [markerIcon, setMarkerIcon] = useState(L.divIcon)

  useEffect(() => {
    setMarkerIcon(
      L.divIcon({
        iconSize: window.innerWidth > 1023 ? [60, 60] : [30, 30],
        iconAnchor: window.innerWidth > 1023 ? [30, 60] : [15, 30],
        html: '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0" y="0" viewBox="0 0 512 512" style="enable-background:new 0 0 512 512;" xml:space="preserve"><path d="M256,0C153.755,0,70.573,83.182,70.573,185.426c0,126.888,165.939,313.167,173.004,321.035 c6.636,7.391,18.222,7.378,24.846,0c7.065-7.868,173.004-194.147,173.004-321.035C441.425,83.182,358.244,0,256,0z M256,278.719 c-51.442,0-93.292-41.851-93.292-93.293S204.559,92.134,256,92.134s93.291,41.851,93.291,93.293S307.441,278.719,256,278.719z"/></svg>',
      }),
    )
  }, [])

  return (
    <Container>
      <Separator />
      <Fade bottom distance="3.75rem">
        <>
          <Head>
            {siteName ? <SiteName>{siteName}</SiteName> : null}
            {address ? (
              <Address dial={5} row>
                <Icon.MapPin />
                {address}
              </Address>
            ) : null}
          </Head>
          <Center>
            <Wrapper row wrap stretch>
              {image ? (
                <Images>
                  <LazyLoad>
                    <Image style={{ backgroundImage: `url(${image})` }} />
                    <Image style={{ backgroundImage: `url(${image})` }} />
                  </LazyLoad>
                </Images>
              ) : null}
              <Map>
                {typeof window !== undefined && latitude && longitude ? (
                  <MapContainer
                    center={[Number(latitude), Number(longitude)]}
                    dragging={L.Browser && L.Browser.mobile ? false : true}
                    zoom={Number(zoom)}
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                    />
                    <Marker
                      icon={markerIcon}
                      position={[Number(latitude), Number(longitude)]}
                    >
                      {siteName ? <Popup>{siteName}</Popup> : null}
                    </Marker>
                  </MapContainer>
                ) : null}
                {googleMapsURL ? (
                  <GoogleMapsCTA
                    arrow
                    label={useVocabularyData(
                      'open-in-google-maps',
                      languageCode,
                    )}
                    URL={googleMapsURL}
                    rel="noreferrer"
                    target="_blank"
                  />
                ) : null}
              </Map>
            </Wrapper>
          </Center>
          <Bottom>
            <HowToReach>
              {title ? <Title>{title}</Title> : null}
              {directions ? (
                <Tabs>
                  <TabList>
                    {directions.map((item: any, index: number) => (
                      <Tab key={index}>
                        <TabIcon icon={item.icon} />
                        {item.title}
                      </Tab>
                    ))}
                  </TabList>

                  {directions.map((item: any, index: number) => (
                    <TabPanel
                      key={index}
                      dangerouslySetInnerHTML={{ __html: item.description }}
                    />
                  ))}
                </Tabs>
              ) : null}
            </HowToReach>
          </Bottom>
        </>
      </Fade>
    </Container>
  )
})

const Container = styled.section`
  max-width: 84rem;
  margin: 6rem auto 9.75rem;
  padding: 0 1.5rem;

  @media (max-width: 1023px) {
    margin: 3.75rem auto 7.5rem;
  }
`

const Head = styled.div`
  max-width: 53.5rem;
  border: 2px solid ${({ theme }) => theme.colors.variants.neutralLight3};
  border-bottom: 0;
  margin: auto;
  padding: 3.75rem 1.5rem;
  text-align: center;

  @media (max-width: 1023px) {
    max-width: 80%;
    padding: 2.25rem 1.5rem;
  }

  @media (max-width: 767px) {
    max-width: none;
    border: 0;
    padding: 0;
  }
`

const SiteName = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.375rem;
  font-weight: 500;
  line-height: 2.9375rem;
`

const Address = styled(FlexBox)`
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.9375rem;
  line-height: 1.125rem;
  margin-top: 1.125rem;
  svg {
    width: auto;
    height: 1.25rem;
    stroke: ${({ theme }) => theme.colors.variants.primaryDark1};
    stroke-width: 1.5;
    margin-right: 0.75rem;
  }
`

const Center = styled.div`
  position: relative;
  &:before {
    content: '';
    width: 100%;
    height: 50%;
    background: ${({ theme }) => theme.colors.variants.neutralLight3};
    position: absolute;
    bottom: 0;
    left: 0;
  }

  @media (max-width: 767px) {
    margin-top: 3rem;
    &:before {
      display: none;
    }
  }
`

const Bottom = styled.div`
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
`

const Wrapper = styled(FlexBox)`
  max-width: 67.25rem;
  margin: auto;
`

const Images = styled.div`
  width: 38%;
  position: relative;

  @media (max-width: 767px) {
    display: none;
  }
`

const Image = styled.span`
  width: 100%;
  height: 100%;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  position: absolute;
  top: 0;
  left: 0;
  &:first-of-type {
    &:before {
      content: '';
      width: 100%;
      height: 100%;
      opacity: 0.7;
      background: ${({ theme }) => theme.colors.variants.primaryDark1};
      position: absolute;
      top: 0;
      left: 0;
      z-index: 2;
    }
  }
  &:last-of-type {
    width: calc(100% - 6rem);
    height: calc(100% - 6rem);
    top: 3rem;
    left: 3rem;
    z-index: 2;
  }
`

const Map = styled.div`
  width: 62%;
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  padding-bottom: 46%;
  position: relative;

  @media (max-width: 767px) {
    width: 100%;
    padding-bottom: 80%;
    .leaflet-left {
      display: none;
    }
  }

  .leaflet-container {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
  .leaflet-marker-icon {
    background: none;
    border: 0;
    svg {
      fill: ${({ theme }) => theme.colors.variants.primaryDark1};
    }
  }
  .leaflet-left {
    top: auto;
    bottom: 0;
    .leaflet-control {
      border: 0;
      border-radius: 0;
      margin: 0;
      a {
        width: 3rem;
        height: 3rem;
        line-height: 2.875rem;
        background: ${({ theme }) => theme.colors.variants.neutralLight4};
        color: ${({ theme }) => theme.colors.variants.neutralDark1};
        font-family: ${({ theme }) => theme.fontFamily.paragraph};
        font-weight: 100;
        font-size: 2.375rem;
        border: 0;
        border-radius: 0;
        transition: 0.3s ease-out;
        &.leaflet-control-zoom-in {
          order: 2;
        }
        &.leaflet-disabled {
          opacity: 0.6;
          pointer-events: none;
        }
        &:hover {
          background: ${({ theme }) => theme.colors.variants.neutralLight3};
        }
      }
    }
  }
  .leaflet-bottom {
    display: none;
  }
  .leaflet-popup {
    .leaflet-popup-content-wrapper {
      background: ${({ theme }) => theme.colors.variants.primaryDark1};
      box-shadow: none;
      border-radius: 0;
      color: ${({ theme }) => theme.colors.variants.neutralLight4};
      font-family: ${({ theme }) => theme.fontFamily.heading};
      font-size: 1rem;
      padding: 1rem;
    }
    .leaflet-popup-content {
      line-height: 1.5rem;
      margin: 0;
    }
    .leaflet-popup-tip {
      background: ${({ theme }) => theme.colors.variants.primaryDark1};
      box-shadow: none;
    }
    .leaflet-popup-close-button {
      display: none;
    }
  }
`

const GoogleMapsCTA = styled(Button)`
  background: ${({ theme }) => theme.colors.variants.neutralDark4};
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: 400;

  @media (max-width: 767px) {
    left: 0;
    justify-content: center;
  }
`

const HowToReach = styled.div`
  max-width: 54.25rem;
  margin: auto;
  padding: 3.75rem 1.5rem 7.5rem;
  overflow: hidden;
  position: relative;
  text-align: center;

  .react-tabs {
    font-family: ${({ theme }) => theme.fontFamily.paragraph};
    .react-tabs__tab-list {
      border: 0;
    }
    .react-tabs__tab {
      display: inline-flex;
      align-items: center;
      border: 0;
      color: ${({ theme }) => theme.colors.variants.neutralDark4};
      font-size: 0.8125rem;
      letter-spacing: 1px;
      line-height: 1.125rem;
      margin: 0 1.125rem;
      padding: 0;
      transition: 0.3s ease-out;
      &:last-of-type {
        &:after {
          display: none;
        }
      }
      &:after {
        content: '';
        width: 2px;
        height: 12px;
        margin-left: 2.25rem;
        background: ${({ theme }) => theme.colors.variants.neutralLight2};
      }
      &:focus {
        box-shadow: none;
      }
      &:hover {
        color: ${({ theme }) => theme.colors.variants.neutralDark1};
      }
      &.react-tabs__tab--selected {
        background: none;
        border-radius: 0;
        color: ${({ theme }) => theme.colors.variants.primaryDark1};
        svg {
          fill: ${({ theme }) => theme.colors.variants.primaryDark1};
        }
      }
    }
    .react-tabs__tab-panel {
      color: ${({ theme }) => theme.colors.variants.neutralDark4};
      font-size: 0.9375rem;
      line-height: 1.875rem;
      margin-top: 3.75rem;
      &.react-tabs__tab-panel--selected {
        animation: fadeInPanel 0.6s;
      }
    }
  }

  @media (max-width: 1023px) {
    padding: 3rem 1.5rem 3.75rem;
    .react-tabs {
      .react-tabs__tab {
        margin-top: 3rem;
      }
    }
  }

  @media (max-width: 767px) {
    .react-tabs {
      .react-tabs__tab {
        margin: 0 0.625rem;
        &:after {
          margin-left: 1rem;
        }
      }
      .react-tabs__tab-panel {
        margin-top: 2.25rem;
      }
    }
  }

  @keyframes fadeInPanel {
    from {
      opacity: 0;
      transform: translateY(2.25rem);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

const Title = styled.h3`
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.375rem;
  font-weight: 500;
  line-height: 2.9375rem;
  margin-bottom: 3.75rem;

  @media (max-width: 1023px) {
    margin-bottom: 3rem;
  }
`
